import React, { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '../utils/formatUtils';
import TermsBadge from './TermsBadge';
import { calculateRecipeCostDisplay } from '../utils/recipePricing';
import { API_BASE_URL } from '../config';
import { getRecipeMissingNames } from '../utils/recipeAvailability';
const PRICE_HINT_TEXT = 'Evde maaliyet ve dışarıdan maaliyet fiyatları varsayımdır. Sizlerin alışveriş sonrası fiyat girişleri ile sistem doğru sonuç vermeye başlayacaktır.';

const formatTlNoCents = (value) => {
  const n = Number(value || 0);
  return `${Number.isFinite(n) ? Math.round(n) : 0} ₺`;
};

const getIngredients = (recipe = {}) => {
  if (Array.isArray(recipe?.scaledIngredients) && recipe.scaledIngredients.length > 0) return recipe.scaledIngredients;
  if (Array.isArray(recipe?.malzemeler) && recipe.malzemeler.length > 0) return recipe.malzemeler;
  if (Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0) return recipe.ingredients;
  return [];
};

const getMissingNames = (recipe = {}) => {
  return getRecipeMissingNames(recipe);
};

const toLowerTr = (v = '') => String(v || '').toLocaleLowerCase('tr-TR');

const isMissingByName = (name = '', missing = []) => {
  if (!name) return false;
  const n = toLowerTr(name);
  return missing.some((m) => {
    const x = toLowerTr(m);
    return x.includes(n) || n.includes(x);
  });
};

const lineText = (ing = {}, mode = 'traditional') => {
  if (typeof ing === 'string') return ing;
  if (mode === 'global') {
    return ing.displayTextGlobal || `${Math.round(Number(ing.quantity || ing.amount || ing.miktar || 0))} ${ing.unit || ing.birim || ''} ${ing.name || ''}`.trim();
  }
  return ing.displayTextTrTraditional || ing.turkmutfagi || `${ing.turkmutfagi_miktar || ing.amountRaw || ''} ${ing.turkmutfagi_birim || ing.unitRaw || ''} ${ing.name || ''}`.replace(/\s+/g, ' ').trim();
};

const getSteps = (recipe = {}) => {
  if (Array.isArray(recipe?.instructions) && recipe.instructions.length > 0) return recipe.instructions;
  if (Array.isArray(recipe?.steps) && recipe.steps.length > 0) return recipe.steps;
  return [];
};

const detectMealType = (recipe = {}) => {
  const tokens = [
    recipe?.serveStyle,
    recipe?.category,
    recipe?.mealType,
    recipe?.dishType,
    recipe?.recipeType,
    recipe?.name,
    recipe?.title
  ]
    .map((x) => String(x || '').toLocaleLowerCase('tr-TR'))
    .join(' ');
  if (/(corba|çorba|soup|sulu)/.test(tokens)) return 'soup';
  if (/(salata|salad|meze)/.test(tokens)) return 'salad';
  if (/(ana yemek|anayemek|main|yemek|et|tavuk)/.test(tokens)) return 'main';
  return 'other';
};

const resolveStepText = (step = {}, level = 'acemi') => {
  if (typeof step === 'string') return step;
  const ordered = level === 'sef'
    ? [step?.detay_sef, step?.detay_usta, step?.detay_acemi]
    : level === 'usta'
      ? [step?.detay_usta, step?.detay_acemi, step?.detay_sef]
      : [step?.detay_acemi, step?.detay_usta, step?.detay_sef];
  return ordered.find((x) => typeof x === 'string' && x.trim()) || step?.islem || step?.text || '';
};
const resolveBabyName = (userProfile = {}) => {
  const raw = userProfile?.babyName || userProfile?.bebek_ismi || userProfile?._babyNameFallback || '';
  const normalized = String(raw || '').trim();
  return normalized.length > 0 ? normalized : 'bebeğiniz';
};

const normalizeLandingStepText = (text = '', userProfile = {}) => {
  const babyName = resolveBabyName(userProfile);
  return String(text || '').replace(/\{?\s*bebek_ismi\s*\}?/gi, babyName);
};

const portionText = (recipe = {}, forcedPortion = null) => `${Number(forcedPortion || recipe?.servings || recipe?.portion || recipe?.kisi_sayisi || recipe?.serving_count || 4)} kişilik`;

const parseMacro = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const m = String(value).replace(',', '.').match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : 0;
};

const getNutrition = (recipe = {}) => {
  const n = recipe?.nutrition || recipe?.nutritionalInfo || recipe?.nutritionalValues || recipe?.besinDegeri || {};
  return {
    protein: parseMacro(n?.protein ?? n?.protein_g ?? recipe?.protein ?? recipe?.proteinGrams),
    carbs: parseMacro(n?.carbs ?? n?.carb ?? n?.carbohydrate ?? recipe?.carbs ?? recipe?.carbGrams),
    fat: parseMacro(n?.fat ?? n?.fat_g ?? recipe?.fat ?? recipe?.fatGrams),
    calories: parseMacro(n?.calories ?? n?.kcal ?? recipe?.calories ?? recipe?.totalCalories)
  };
};

const getChefNote = (recipe = {}) => {
  const candidates = [
    recipe?.chefTip,
    recipe?.chef_note,
    recipe?.chefNote,
    recipe?.sef_notu,
    recipe?.sefinNotu,
    recipe?.presentation?.chefTip,
    recipe?.presentation?.chef_note,
    recipe?.presentation?.chefNote,
    recipe?.insight?.chefTip,
    recipe?.insight?.chef_note,
    recipe?.insight?.chefNote,
    recipe?.metadata?.chefTip,
    recipe?.metadata?.chef_note,
    recipe?.metadata?.chefNote,
    recipe?.sefOnerisi,
    recipe?.şefNotu,
    recipe?.chef?.note,
    recipe?.notes?.chef,
    recipe?.bebek_notu?.mesaj,
    recipe?.specialProtocols?.chef_note,
    recipe?.specialProtocols?.chefNote
  ];
  const found = candidates.find((x) => typeof x === 'string' && x.trim());
  return found ? String(found).trim() : '';
};

const getStorageZone = (name = '', ingredient = {}) => {
  // [Storage tek-kaynak] Backend StorageZonePolicy'den gelen kanonik rafı KULLAN (doğru + KN-portable).
  // Frontend heuristik yalnız FALLBACK (backend annotasyonu yoksa).
  const backendShelf = ingredient?.storageZone;
  if (typeof backendShelf === 'string' && backendShelf.trim()) return backendShelf.trim();

  const n = toLowerTr(name);
  const klass = toLowerTr(ingredient?.ingredientClass || ingredient?.category || ingredient?.ingredientType || '');

  if (klass.includes('baharat') || n.includes('tuz') || n.includes('karabiber') || n.includes('pul biber')) return 'Baharatlık';
  if (n.includes('dondur') || n.includes('bezelye') || n.includes('dondurulmuş')) return 'Dondurucu';
  // İşlenmiş şarküteri + süt/et/balık → Buzdolabı (sucuk/salam substring 'et' içermez → açıkça ekle)
  if (n.includes('sucuk') || n.includes('salam') || n.includes('sosis') || n.includes('pastırma') || n.includes('jambon') || n.includes('şarküteri') ||
      n.includes('tavuk') || n.includes('et') || n.includes('balık') || n.includes('yoğurt') || n.includes('süt') || n.includes('sut') || n.includes('yumurta') || n.includes('peynir') || n.includes('tereyağ')) return 'Buzdolabı';
  if (n.includes('salatalık') || n.includes('domates') || n.includes('biber') || n.includes('havuç') || n.includes('kabak') || n.includes('maydanoz') || n.includes('limon')) return 'Sebzelik';
  if (n.includes('zeytinyağ') || n.includes('sıvı yağ') || n.includes('sivi yag') || n.includes('un') || n.includes('pirinç') || n.includes('pirinc') || n.includes('bakliyat')) return 'Kiler';
  if (klass.includes('staple')) return 'Kiler';
  return 'Kiler';
};

export default function RecipeInsightModalDesktop(props) {
  const { isOpen, onClose, recipe, isDarkMode, onAddToShoppingList, userPortion, onUsedSuccess, userProfile } = props;
  const [presentationMode, setPresentationMode] = useState('traditional');
  const [detailView, setDetailView] = useState('insight');
  const [skillLevel, setSkillLevel] = useState('acemi');
  const [showPriceHint, setShowPriceHint] = useState(false);
  const [markingAsUsed, setMarkingAsUsed] = useState(false);
  const [collapsed, setCollapsed] = useState({
    cost: false,
    storage: false,
    nutrition: false,
    safety: false,
    upcoming: false
  });

  const ingredients = useMemo(() => getIngredients(recipe), [recipe]);
  const missingNames = useMemo(() => getMissingNames(recipe), [recipe]);
  const steps = useMemo(() => getSteps(recipe), [recipe]);
  const hasBabySignal = Boolean(
    userProfile?.hasBaby ||
    userProfile?.bebek_var_mi ||
    userProfile?.babyAgeGroup ||
    userProfile?.bebek_yas_grubu ||
    userProfile?.babyBirthDate ||
    userProfile?.babyName
  );
  const babySafetyText = normalizeLandingStepText(
    recipe?.babyAdjustment ||
    recipe?.babyInfo ||
    recipe?.babyNote ||
    'Tuz ve baharat eklemeden önce bebeğiniz için bir porsiyonu ayırıp kenara alın.',
    userProfile
  );
  const adaptiveSteps = useMemo(() => {
    const list = Array.isArray(steps) ? [...steps] : [];
    if (!hasBabySignal) return list;
    const hasBabyStep = list.some((step) => {
      const text = toLowerTr(resolveStepText(step, skillLevel));
      const type = toLowerTr(step?.type || '');
      return type === 'portion_split' || text.includes('bebek') || text.includes('porsiyon ayır') || text.includes('kenara ayır');
    });
    if (hasBabyStep) return list;
    list.unshift({
      type: 'portion_split',
      detay_acemi: babySafetyText,
      detay_usta: babySafetyText,
      detay_sef: babySafetyText
    });
    return list;
  }, [steps, hasBabySignal, babySafetyText, skillLevel]);
  const nutrition = useMemo(() => getNutrition(recipe), [recipe]);
  const chefNote = useMemo(() => getChefNote(recipe), [recipe]);
  const hasBrandRiskWarning = Boolean(recipe?._hasAllergenCollision);
  const hasGeneralWarning = !hasBrandRiskWarning && (
    (Array.isArray(recipe?.safetyFlags) && recipe.safetyFlags.length > 0) ||
    (Array.isArray(recipe?.safetyWarnings) && recipe.safetyWarnings.length > 0) ||
    recipe?.isBabySafe === false
  );
  useEffect(() => {
    if (isOpen) setDetailView('insight');
  }, [isOpen, recipe?.id]);

  const effectivePortion = Math.max(1, Number(userPortion || recipe?.targetPortion || recipe?.presentation?.appliedPortion || recipe?.servings || 1));
  const { displayedCostBreakdown, displayedCostTotal } = calculateRecipeCostDisplay(recipe, effectivePortion);
  const total = Number(displayedCostTotal || recipe?.priceSnapshot?.total || recipe?.totalCost || recipe?.presentation?.totalCost || 0);
  const weightedMatchRatio = Number(recipe?.insight?.match?.weightedMatchRatio || 0);
  // [Fix] Çember "%EVDE MEVCUT" — GÖSTERİLEN sayılarla (evde/toplam malzeme) tutarlı türet.
  // Önceden ayrı weightedMatchRatio alanı bazı veri yollarında 0 geliyordu → çember %0 derken
  // "4 Malzeme Evde" diyordu (çelişki). Artık aynı ingredients/missingNames kaynağından hesaplanır.
  const _homeCount = ingredients.filter((ing) => {
    const _n = typeof ing === 'string' ? ing : (ing?.name || '');
    return !isMissingByName(_n, missingNames);
  }).length;
  const matchPercent = ingredients.length > 0
    ? Math.round((_homeCount / ingredients.length) * 100)
    : Math.max(0, Math.min(100, Math.round(weightedMatchRatio * 100)));
  const isFullReady = matchPercent >= 100 && missingNames.length === 0;
  const mealType = detectMealType(recipe);
  const outsideMultiplierByType = { soup: 2.2, main: 2.9, salad: 2.0, other: 2.5 };
  const inferredOutsideFallback = total * (outsideMultiplierByType[mealType] || outsideMultiplierByType.other);
  const outsidePrice = Number(recipe?.priceSnapshot?.outsidePrice ?? recipe?.presentation?.outsidePrice ?? inferredOutsideFallback) || 0;
  const saving = Math.max(0, outsidePrice - total);
  const rescuedCount = Number(recipe?.insight?.skt?.rescuedCount || 0);
  const urgencyScore = Number(recipe?.insight?.skt?.urgencyScore || 0);
  const nearExpiryRows = useMemo(
    () => ingredients.filter((x) => Number(x?.remainingDays || x?.daysRemaining || 0) > 0).slice(0, 4),
    [ingredients]
  );

  const available = ingredients.filter((ing) => {
    const n = typeof ing === 'string' ? ing : (ing?.name || '');
    return !isMissingByName(n, missingNames);
  });
  const storageRows = useMemo(() => {
    const rows = ingredients.map((ing, idx) => {
      const name = typeof ing === 'string' ? ing : (ing?.name || `Malzeme ${idx + 1}`);
      const zone = getStorageZone(name, ing);
      const missing = isMissingByName(name, missingNames);
      return { key: `${name}-${idx}`, name, zone, missing };
    });
    // Eksikler üstte olsun ki özet ile görünür liste çakışmasın.
    rows.sort((a, b) => Number(b.missing) - Number(a.missing));
    return rows.slice(0, 8);
  }, [ingredients, missingNames]);
  const costByName = useMemo(() => {
    const map = new Map();
    ingredients.forEach((ing, idx) => {
      const name = typeof ing === 'string' ? ing : (ing?.name || '');
      const key = toLowerTr(name || '');
      if (!key) return;
      const scaledLine = Number(displayedCostBreakdown?.[idx] || 0);
      if (scaledLine > 0) {
        map.set(key, scaledLine);
        return;
      }
      const rawRow = (Array.isArray(recipe?.priceSnapshot?.breakdown) ? recipe.priceSnapshot.breakdown : [])
        .find((row) => toLowerTr(row?.name || '') === key);
      map.set(key, Number(rawRow?.total || 0));
    });
    return map;
  }, [ingredients, displayedCostBreakdown, recipe]);

  const modeCard = isDarkMode ? 'border border-[#2a3348] bg-[#171f33] text-[#dae2fd]' : 'border border-slate-200 bg-white text-slate-900 shadow-sm';
  const modalBg = isDarkMode ? 'bg-[#0b1326] text-[#dae2fd] border-[#2a3348]' : 'bg-[#f8fafc] text-slate-900 border-slate-200';
  const mutedText = isDarkMode ? 'text-[#bccbb9]' : 'text-slate-500 font-medium';
  const heroCard = isDarkMode ? 'bg-[#171f33] border-[#2a3348]' : 'bg-white border-slate-200 shadow-md';
  const rowBg = isDarkMode ? 'bg-white/5' : 'bg-slate-50 border border-slate-100';

  const handleAddMissing = () => {
    if (typeof onAddToShoppingList === 'function') {
      onAddToShoppingList(missingNames);
    }
  };
  const handleMarkAsUsed = async () => {
    if (markingAsUsed) return;
    setMarkingAsUsed(true);
    try {
      const token = localStorage.getItem('authToken');
      const usedPortion = Math.max(1, Number(userPortion || recipe?.targetPortion || recipe?.presentation?.appliedPortion || recipe?.servings || 1));
      const response = await fetch(`${API_BASE_URL}/api/recipes/used`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          recipeData: recipe,
          rating: null,
          usedPortion,
          cookingNotes: `Cooked for ${usedPortion} portions.`
        })
      });
      if (!response.ok) throw new Error('recipe_used_failed');
      if (typeof onUsedSuccess === 'function') onUsedSuccess(recipe);
      onClose();
    } catch (_) {
      // Silent failure fallback: keep modal open for retry.
    } finally {
      setMarkingAsUsed(false);
    }
  };
  const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  if (!isOpen || !recipe) return null;

  return (
    <div className="fixed inset-0 z-[260] bg-[#060e20]/35 backdrop-blur-md p-3 lg:p-6" onClick={onClose}>
      <div className={`relative mx-auto h-[94vh] w-full max-w-[1420px] overflow-hidden rounded-3xl border ${modalBg}`} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className={`absolute right-4 top-4 z-30 rounded-full p-2 text-xl leading-none ${isDarkMode ? 'text-[#dae2fd]/80 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100'}`}>✕</button>
        <div className="h-full overflow-y-auto px-4 pb-24 pt-4 lg:px-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
            <aside className="w-full shrink-0 space-y-4 lg:w-[330px]">
              <div className="flex items-center justify-between">
                <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold ${isDarkMode ? 'bg-red-500/15 text-red-500' : 'bg-red-50 text-red-600'}`}>
                  <span className="material-symbols-outlined text-sm fill-1">stars</span>
                  SANA ÖZEL ÖNERİ
                </div>
                <div />
              </div>
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-4xl font-black leading-[1.03] tracking-tight text-on-surface">{recipe?.name || recipe?.title || 'Tarif'}</h2>
                  <div className="mt-1 flex items-center gap-1.5">
                    <TermsBadge className="!static !top-auto !right-auto" />
                    {hasBrandRiskWarning && (
                      <div
                        title="Mutfaktaki ürünle çakışan alerjen/risk uyarısı var"
                        className={`inline-flex size-8 items-center justify-center rounded-full border font-black ${isDarkMode ? 'border-red-500/40 bg-red-500/20 text-red-300' : 'border-red-200 bg-red-50 text-red-600'}`}
                      >
                        !
                      </div>
                    )}
                    {hasGeneralWarning && (
                      <div
                        title="Tarif için genel kullanım/güvenlik uyarısı var"
                        className={`inline-flex size-8 items-center justify-center rounded-full border font-black ${isDarkMode ? 'border-amber-500/40 bg-amber-500/20 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
                      >
                        !
                      </div>
                    )}
                  </div>
                </div>
                <p className={`text-sm ${mutedText}`}>Dolabındaki malzemelerle, tam zamanı!</p>
              </div>
              <div className={`relative group rounded-2xl overflow-hidden border ${heroCard}`}>
                <img src={String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim().startsWith('/uploads/') || String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim().startsWith('/api/uploads/') ? `${API_BASE_URL}${String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim()}` : String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim().startsWith('uploads/') ? `${API_BASE_URL}/${String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim()}` : (recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg')} alt={recipe?.name || 'Tarif görseli'} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute bottom-3 left-3 right-3 bg-white/90 backdrop-blur-md p-2.5 rounded-xl flex items-center gap-3 border border-black/5 shadow-lg">
                  <div className="w-9 h-9 bg-red-500/10 rounded-lg flex items-center justify-center text-red-500">
                    <span className="material-symbols-outlined fill-1 text-xl">local_fire_department</span>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-red-500 uppercase tracking-widest leading-none mb-0.5">BUGÜN YAPMANIN</p>
                    <p className="text-xs text-slate-800 font-bold uppercase leading-none">En mantıklı zamanı</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className={`${modeCard} rounded-2xl p-2.5 text-center transition-all hover:border-red-200`}><span className="material-symbols-outlined text-red-500 text-lg">schedule</span><div className={`text-[9px] uppercase ${mutedText}`}>Hazırlık</div><div className="text-2xl font-black">{recipe?.cookTime || recipe?.cookingTime || 25}</div><div className="text-[11px]">dk</div></div>
                <div className={`${modeCard} rounded-2xl p-2.5 text-center transition-all hover:border-red-200`}><span className="material-symbols-outlined text-red-500 text-lg">groups</span><div className={`text-[9px] uppercase ${mutedText}`}>Porsiyon</div><div className="text-lg font-black">{portionText(recipe, effectivePortion)}</div></div>
                <div className={`${modeCard} rounded-2xl p-2.5 text-center transition-all hover:border-red-200`}><span className="material-symbols-outlined text-red-500 text-lg">bar_chart</span><div className={`text-[9px] uppercase ${mutedText}`}>Zorluk</div><div className="text-lg font-black">{recipe?.difficulty || 'Orta'}</div></div>
              </div>

              <div className={`${modeCard} rounded-2xl p-4`}>
                <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-500"><span className="material-symbols-outlined text-base fill-1">nutrition</span>Besin Değerleri</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className={`rounded-xl p-2 ${rowBg}`}><div className={`text-[8px] font-bold uppercase ${mutedText}`}>Protein</div><div className="text-sm font-black">{Number(nutrition?.protein || 0)}g</div></div>
                  <div className={`rounded-xl p-2 ${rowBg}`}><div className={`text-[8px] font-bold uppercase ${mutedText}`}>K.hidrat</div><div className="text-sm font-black">{Number(nutrition?.carbs || 0)}g</div></div>
                  <div className={`rounded-xl p-2 ${rowBg}`}><div className={`text-[8px] font-bold uppercase ${mutedText}`}>Yağ</div><div className="text-sm font-black">{Number(nutrition?.fat || 0)}g</div></div>
                  <div className={`rounded-xl p-2 ${rowBg}`}><div className={`text-[8px] font-bold uppercase ${mutedText}`}>Kalori</div><div className="text-sm font-black">{Number(nutrition?.calories || 0)}</div></div>
                </div>
              </div>

              <div className={`${modeCard} rounded-2xl p-4`}>
                <div className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-amber-500"><span className="material-symbols-outlined text-base fill-1">lightbulb</span>Neden Bu Tarif?</div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3"><span className="material-symbols-outlined text-red-500 text-sm fill-1 mt-0.5">check_circle</span><span className="leading-relaxed">Evdeki malzemelerin <b>%{matchPercent}</b> kullanılıyor.</span></div>
                  <div className="flex items-start gap-3"><span className="material-symbols-outlined text-red-500 text-sm fill-1 mt-0.5">check_circle</span><span className="leading-relaxed"><b className="text-red-600">{missingNames.length}</b> malzeme eksik.</span></div>
                  <div className="flex items-start gap-3"><span className="material-symbols-outlined text-red-500 text-sm fill-1 mt-0.5">check_circle</span><span className="leading-relaxed">Dışarıya göre <b>{formatTlNoCents(saving)}</b> tasarruf.</span></div>
                  <div className="flex items-start gap-3"><span className="material-symbols-outlined text-red-500 text-sm fill-1 mt-0.5">check_circle</span><span className="leading-relaxed">SKT etkisi: <b>{rescuedCount}</b> ürün, skor <b>{urgencyScore.toFixed(2)}</b></span></div>
                </div>
              </div>
            </aside>

            <section className="min-w-0 flex-1 space-y-4">
              <div className={`${modeCard} rounded-3xl p-6 relative overflow-hidden`}>
                <div className="flex flex-wrap items-end justify-between gap-6 relative z-10">
                  <div>
                    <div className={`text-xs font-black uppercase tracking-widest ${mutedText}`}>Bu tarif sana bugün</div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-[56px] font-black text-red-500 leading-none">{Math.round(Number(saving || 0))}</span>
                      <span className="text-2xl font-bold text-red-500">₺</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-2xl font-black uppercase tracking-tight">
                      <span>kazandırıyor! 🎉</span>
                      <div className="relative">
                        <button
                          type="button"
                          title={PRICE_HINT_TEXT}
                          onClick={() => setShowPriceHint((prev) => !prev)}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-red-400/60 bg-red-500/20 text-[11px] font-black text-red-300"
                        >
                          !
                        </button>
                        {showPriceHint && (
                          <div className={`absolute bottom-full left-0 mb-2 z-30 w-72 rounded-xl border p-2 text-[11px] normal-case leading-relaxed shadow-xl ${isDarkMode ? 'border-slate-600 bg-slate-900 text-slate-200' : 'border-slate-200 bg-white text-slate-700'}`}>
                            {PRICE_HINT_TEXT}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-10">
                    <div><div className={`text-[10px] font-bold uppercase mb-1 ${mutedText}`}>DIŞARIDA YESEN</div><div className={`text-2xl font-black ${isDarkMode ? 'text-rose-400' : 'text-rose-500'}`}>≈ {formatTlNoCents(outsidePrice)}</div></div>
                    <div><div className={`text-[10px] font-bold uppercase mb-1 ${mutedText}`}>EVDE MALİYETİN</div><div className="text-2xl font-black text-red-500">{formatTlNoCents(total)}</div></div>
                  </div>
                </div>
              </div>

              {detailView === 'insight' ? (
                <div className="flex flex-col gap-5 xl:flex-row">
                  <div className="flex flex-1 flex-col gap-5">
                    <div className={`${modeCard} rounded-2xl p-4`}>
                      <div className="mb-6 flex items-center gap-2 text-xs font-black uppercase tracking-widest"><span className={`material-symbols-outlined text-base ${isFullReady ? 'text-emerald-500' : 'text-red-500'}`}>inventory_2</span>Malzeme Uyumu</div>
                      <div className="flex items-center gap-8">
                        <div className="relative h-32 w-32">
                          <svg className="w-full h-full transform -rotate-90">
                            <circle className={isDarkMode ? 'text-white/5' : 'text-slate-100'} cx="64" cy="64" fill="transparent" r="58" stroke="currentColor" strokeWidth="10"></circle>
                            <circle className={isFullReady ? 'text-emerald-500' : 'text-red-500'} cx="64" cy="64" fill="transparent" r="58" stroke="currentColor" strokeDasharray="364.4" strokeDashoffset={364.4 - (364.4 * matchPercent) / 100} strokeLinecap="round" strokeWidth="10"></circle>
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-black">%{matchPercent}</span>
                            <span className="text-[8px] font-black uppercase text-center leading-tight opacity-60">evde mevcut</span>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full shadow-sm ${isFullReady ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                            <div><div className="text-sm font-black">{available.length} Malzeme Evde</div><div className={`text-[10px] ${mutedText}`}>Pişirmeye hazır!</div></div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-2.5 h-2.5 rounded-full bg-amber-400 shadow-sm"></div>
                            <div><div className="text-sm font-black text-red-600">{missingNames.length} Malzeme Eksik</div><div className={`text-[10px] ${mutedText}`}>Listeye eklenebilir.</div></div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleAddMissing}
                        disabled={isFullReady}
                        className={`mt-6 w-full rounded-xl py-3 text-[10px] font-black uppercase tracking-widest transition-all ${isFullReady
                          ? (isDarkMode ? 'border border-emerald-500/40 bg-emerald-500/15 text-emerald-400 cursor-default' : 'border border-emerald-200 bg-emerald-50 text-emerald-700 cursor-default shadow-sm')
                          : (isDarkMode ? 'border border-red-500/40 bg-red-500/10 text-red-500 hover:bg-red-500/20' : 'border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 shadow-sm')
                          }`}
                      >
                        <span className="material-symbols-outlined text-sm fill-1 inline-block align-middle mr-1">{isFullReady ? 'check_circle' : 'shopping_cart'}</span> {isFullReady ? 'Tüm malzemeler hazır' : 'Eksikleri Listeye Ekle'}

                      </button>
                    </div>

                    {/* Buzdolabı Yerleşimi */}
                    <div className={`${modeCard} rounded-2xl p-4`}>
                      <div className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-blue-500"><span className="material-symbols-outlined text-base">kitchen</span>Buzdolabı Yerleşimi</div>
                      <div className={`mb-3 flex items-center justify-between rounded-xl px-3 py-2 text-sm ${rowBg}`}>
                        <span>Özet: {available.length} ürün evde, <b className="text-red-600 ml-1">{missingNames.length} eksik</b></span>
                        <button onClick={() => toggle('storage')} className="text-[10px] font-black uppercase text-red-500 hover:underline">{collapsed.storage ? 'Detayı Aç' : 'Detayı Kapat'}</button>
                      </div>
                      {!collapsed.storage && (
                        <div className="space-y-2 text-sm">
                          {storageRows.map((row) => {
                            return (
                              <div key={row.key} className={`flex items-center justify-between rounded-xl p-3 ${rowBg}`}>
                                <span className={`font-medium ${row.missing ? 'text-red-600 font-bold' : ''}`}>{row.name}</span>
                                <span className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${row.missing ? 'bg-red-500 text-white' : isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                  <span className="material-symbols-outlined text-[14px]">{row.missing ? 'error' : 'kitchen'}</span>
                                  {row.zone}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sağ Sütun: Maliyet, Uyarılar ve Güvenlik */}
                  <div className="flex flex-1 flex-col gap-5">
                    {/* Maliyet Analizi */}
                    <div className={`${modeCard} rounded-2xl p-4`}>
                      <div className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-indigo-500"><span className="material-symbols-outlined text-base">analytics</span>Maliyet Analizi</div>
                      <div className={`mb-3 flex items-center justify-between rounded-xl px-3 py-2 text-sm ${rowBg}`}>
                        <span>Özet: Toplam {formatTlNoCents(total)}</span>
                        <button onClick={() => toggle('cost')} className="text-[10px] font-black uppercase text-red-500 hover:underline">{collapsed.cost ? 'Detayı Aç' : 'Detayı Kapat'}</button>
                      </div>
                      {!collapsed.cost && (
                        <div className="space-y-1.5 text-sm">
                          <div className={`flex justify-between items-center text-[10px] font-bold uppercase ${mutedText} border-b border-black/5 pb-1 mb-1`}>
                            <span>MALZEME</span>
                            <span>MALİYET</span>
                          </div>
                          {ingredients.slice(0, 8).map((ing, idx) => {
                            const n = typeof ing === 'string' ? ing : (ing?.name || `Malzeme ${idx + 1}`);
                            const cost = costByName.get(toLowerTr(n));
                            const displayCost = Number.isFinite(cost) ? formatCurrency(cost) : 'Fiyat Yok';
                            const miss = isMissingByName(n, missingNames);
                            return (
                              <div key={`${n}-${idx}`} className="flex items-center justify-between py-1 border-b border-black/5 last:border-0">
                                <span className={`flex items-center gap-2 ${miss ? 'text-red-600 font-bold' : 'font-medium'}`}>
                                  <div className={`w-1.5 h-1.5 rounded-full ${miss ? 'bg-red-600' : 'bg-red-500'}`}></div>
                                  {n}
                                </span>
                                <span className={`font-black ${miss ? 'text-red-600' : ''}`}>{displayCost}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="mt-4 flex items-center justify-between border-t border-black/5 pt-4">
                        <span className={`text-xs font-bold uppercase ${mutedText}`}>TAHMİNİ TOPLAM MALİYET</span>
                        <span className="text-2xl font-black text-emerald-500">{formatTlNoCents(total)}</span>
                      </div>
                    </div>

                    {/* Yaklaşan Tarihler */}
                    {nearExpiryRows.length > 0 && (
                      <div className={`${modeCard} rounded-2xl p-4`}>
                        <div className="mb-4 flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-rose-500"><span className="material-symbols-outlined text-base">calendar_month</span>Yaklaşan Tarihler</div>
                          <span className={`text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-widest ${isDarkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>{rescuedCount} KRİTİK</span>
                        </div>
                        <div className={`mb-3 flex items-center justify-between rounded-xl px-3 py-2 text-sm ${rowBg}`}>
                          <span>Özet: {nearExpiryRows.length} sinyal</span>
                          <button onClick={() => toggle('upcoming')} className="text-[10px] font-black uppercase text-red-500 hover:underline">{collapsed.upcoming ? 'Detayı Aç' : 'Detayı Kapat'}</button>
                        </div>
                        {!collapsed.upcoming && (
                          <div className="space-y-3 text-sm">
                            {nearExpiryRows.map((x, i) => {
                              const n = x?.name || `Ürün ${i + 1}`;
                              const d = Number(x?.remainingDays || x?.daysRemaining || 0);
                              return (
                                <div key={`${n}-${i}`} className={`flex items-center justify-between rounded-xl p-3 ${rowBg} ${d <= 1 ? 'border-rose-300 bg-rose-50' : ''}`}>
                                  <span className="font-bold flex items-center gap-3">
                                    <span className="material-symbols-outlined text-lg fill-1 text-rose-500">restaurant</span>
                                    {n}
                                  </span>
                                  <span className={`font-black uppercase text-[10px] ${d <= 1 ? 'text-rose-600' : 'text-amber-500'}`}>{d <= 1 ? 'YARIN SON' : `${d} GÜN KALDI`}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sana Göre Güvenli */}
                    <div className={`${modeCard} rounded-2xl p-4`}>
                      <div className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-purple-500"><span className="material-symbols-outlined text-base">verified_user</span>Sana Göre Güvenli</div>
                      <div className={`mb-3 flex items-center justify-between rounded-xl px-3 py-2 text-sm ${rowBg}`}>
                        <span>Özet: {recipe?._hasAllergenCollision ? 'Dikkat var' : 'Güvenli'}</span>
                        <button onClick={() => toggle('safety')} className="text-[10px] font-black uppercase text-red-500 hover:underline">{collapsed.safety ? 'Detayı Aç' : 'Detayı Kapat'}</button>
                      </div>
                      {!collapsed.safety && (
                        <div className="space-y-3 text-sm">
                          <div className={`flex items-start gap-3 p-3 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
                            <span className={`material-symbols-outlined fill-1 text-xl ${recipe?._hasAllergenCollision ? 'text-amber-500' : 'text-emerald-500'}`}>check_circle</span>
                            <div>
                              <p className="font-black text-[11px] uppercase tracking-wide">Profil Uyumu</p>
                              <p className={`text-[10px] ${mutedText}`}>Bebek güvenliği: <b>{recipe?.isBabySafe === false ? 'Dikkat!' : 'Uygun'}</b></p>
                            </div>
                          </div>
                          <div className={`p-3 rounded-xl flex items-center gap-3 ${isDarkMode ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'}`}>
                            <span className="material-symbols-outlined text-purple-600 text-xl">family_restroom</span>
                            <span className="text-[9px] font-black text-purple-700 uppercase tracking-widest leading-tight">{recipe?.open_action_reason || 'Bu tarif profiliniz için tam güvenli.'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                    <div className={`${modeCard} rounded-2xl p-4`}>
                      <div className="mb-4 flex items-center justify-between">
                        <div className="text-xs font-black uppercase tracking-widest">Malzemeler</div>
                        <div className="flex gap-2 text-xs">
                          <button onClick={() => setPresentationMode('traditional')} className={`rounded-full px-4 py-1.5 font-black text-[10px] uppercase tracking-wider transition-all ${presentationMode === 'traditional' ? 'bg-red-500 text-white shadow-md' : isDarkMode ? 'bg-white/10' : 'bg-slate-100 text-slate-600'}`}>Geleneksel</button>
                          <button onClick={() => setPresentationMode('global')} className={`rounded-full px-4 py-1.5 font-black text-[10px] uppercase tracking-wider transition-all ${presentationMode === 'global' ? 'bg-indigo-500 text-white shadow-md' : isDarkMode ? 'bg-white/10' : 'bg-slate-100 text-slate-600'}`}>Dünya</button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {ingredients.map((ing, idx) => {
                          const n = typeof ing === 'string' ? ing : (ing?.name || '');
                          const miss = isMissingByName(n, missingNames);
                          const missingClass = isDarkMode
                            ? 'bg-rose-500/25 border border-rose-300/30 text-white'
                            : 'bg-rose-50 border border-rose-200 text-rose-700 font-bold shadow-sm';
                          return <div key={`${n}-${idx}`} className={`rounded-xl px-4 py-2.5 text-sm transition-all ${miss ? missingClass : isDarkMode ? 'bg-white/5' : 'bg-slate-50 border border-slate-100'}`}>{lineText(ing, presentationMode)}</div>;
                        })}
                      </div>
                    </div>
                    <div className={`${modeCard} rounded-2xl p-4`}>
                      <div className="mb-4 flex items-center justify-between gap-2">
                        <div className="text-xs font-black uppercase tracking-widest">Tarif Yapilisi</div>
                        <div className="flex items-center gap-1.5">
                          {[
                            { key: 'acemi', label: 'Acemi' },
                            { key: 'usta', label: 'Usta' },
                            { key: 'sef', label: 'Sef' }
                          ].map((item) => (
                            <button
                              key={item.key}
                              onClick={() => setSkillLevel(item.key)}
                              className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider transition-all ${skillLevel === item.key
                                ? 'bg-red-500 text-white shadow-md'
                                : isDarkMode
                                  ? 'bg-white/10 text-[#dae2fd]'
                                  : 'bg-slate-100 text-slate-600'
                                }`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-3">
                        {adaptiveSteps.length === 0 && <div className={`rounded-xl p-4 text-sm ${rowBg}`}>Tarif adımı bulunamadı.</div>}
                        {hasBabySignal && (
                          <div className={`rounded-xl border p-4 text-sm ${isDarkMode ? 'border-[#F59E0B] bg-[#F59E0B]/25 text-[#FFF4DB]' : 'border-[#F59E0B] bg-[#F59E0B]/30 text-black'}`}>
                            Bebek Notu: {babySafetyText}
                          </div>
                        )}
                        {adaptiveSteps.map((step, idx) => (
                          <div key={idx} className={`rounded-xl p-4 text-sm ${rowBg}`}>
                            <div className={`mb-2 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 ${step?.type === 'portion_split' ? (isDarkMode ? 'text-[#FFF4DB]' : 'text-black') : 'text-red-500'}`}>
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] ${step?.type === 'portion_split' ? 'bg-[#F59E0B] text-black' : 'bg-red-500 text-white'}`}>
                                {idx + 1}
                              </span>
                              {step?.type === 'portion_split' ? `👶 Adım ${idx + 1}` : `Adım ${idx + 1}`}
                            </div>
                            <div className="leading-relaxed font-medium">{normalizeLandingStepText(resolveStepText(step, skillLevel), userProfile)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {chefNote && (
                    <div className={`${modeCard} rounded-2xl p-4`}>
                      <div className={`rounded-xl p-4 text-sm border shadow-sm ${isDarkMode ? 'border-amber-300/35 bg-amber-500/12' : 'border-amber-200 bg-amber-50'}`}>
                        <div className={`mb-2 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                          <span className="material-symbols-outlined text-lg fill-1">stars</span>
                          Şefin Notu
                        </div>
                        <div className={`leading-relaxed ${isDarkMode ? 'text-amber-100' : 'text-slate-800 font-medium'}`}>{chefNote}</div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-5 z-20 flex items-center justify-center gap-4 px-6">
          <button
            onClick={() => {
              if (detailView === 'recipe') {
                setDetailView('insight');
              }
            }}
            className={`pointer-events-auto rounded-2xl px-10 py-4 text-sm font-black uppercase tracking-widest transition-all hover:scale-105 active:scale-95 ${isDarkMode ? 'border border-[#2a3348] bg-[#171f33] text-[#dae2fd]' : 'border border-slate-200 bg-white text-slate-900 shadow-lg'}`}
          >
            {detailView === 'insight' ? 'Paylas' : '⬅️'}
          </button>
          <button
            onClick={() => {
              if (detailView === 'insight') {
                setDetailView('recipe');
                return;
              }
              handleMarkAsUsed();
            }}
            className={`pointer-events-auto rounded-2xl px-16 py-5 text-sm font-black text-white transition-all hover:scale-[1.02] active:scale-[0.98] flex flex-col items-center ${detailView === 'insight' && isFullReady
              ? 'bg-emerald-500 shadow-xl shadow-emerald-500/35 animate-pulse'
              : 'bg-red-500 shadow-xl shadow-red-500/25'
              }`}
          >
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined fill-1">menu_book</span>
              {detailView === 'insight' ? 'HEMEN TARİFİ GÖR' : (markingAsUsed ? 'İŞLENİYOR...' : 'KULLANDIM')}
            </div>
            <span className="text-[8px] opacity-70 mt-0.5 tracking-tighter">MUTFAKTA SİHİR BAŞLIYOR</span>
          </button>
          <button className={`pointer-events-auto rounded-2xl px-10 py-4 text-sm font-black uppercase tracking-widest transition-all hover:scale-105 active:scale-95 ${isDarkMode ? 'border border-[#2a3348] bg-[#171f33] text-[#dae2fd]' : 'border border-slate-200 bg-white text-slate-900 shadow-lg'}`}>❤️</button>
        </div>
      </div>
    </div>
  );
}
